import * as globals from '@wandb/common/css/globals.styles';
import {TargetBlank} from '@wandb/common/util/links';
import isUrl from 'is-url';
import React, {useEffect, useMemo, useState} from 'react';

import BarChart from '../components/vis/BarChart';
import * as PlotHelpers from '../util/plotHelpers';
import * as S from './CollapsibleJSONTable.styles';

type RenderDataProps = {
  data: any;
  label: string;
  level?: number;
  ignoreUnderscoreKeys?: boolean;
  searchQuery?: string;
  parentMatchesSearch?: boolean;
  viewRawData?: () => void;
  stringifyJsonValue?: boolean;
};

interface HistogramData {
  _type: 'histogram';
  bins: number[];
  values: number[];
}

const recursiveObjKeys = (obj: {[key: string]: any}) => {
  const keys: Set<string> = new Set();
  const addKeys = (subObj: {[key: string]: any} = obj) => {
    Object.keys(subObj).forEach(k => {
      keys.add(k);
      if (typeof subObj[k] === 'object' && subObj[k] != null) {
        addKeys(subObj[k]);
      }
    });
  };
  addKeys(obj);
  return Array.from(keys);
};

const groupByKeyPrefix = (obj: {[key: string]: any}) => {
  const objKeys = Object.keys(obj).sort();
  const groupedByKeyPrefix = objKeys.reduce((acc, key) => {
    const keyPrefix = key.split('/')[0];
    const keySuffix = key.split('/').slice(1).join('/');
    if (acc[keyPrefix] == null) {
      acc[keyPrefix] = {};
    }
    if (keySuffix && typeof acc[keyPrefix] !== 'object') {
      const value = acc[keyPrefix];
      acc[keyPrefix] = {};
      acc[keyPrefix][''] = value;
    }
    if (
      keySuffix &&
      typeof acc[keyPrefix] === 'object' &&
      acc[keyPrefix] != null &&
      !Array.isArray(acc[keyPrefix])
    ) {
      try {
        acc[keyPrefix][keySuffix] = obj[key];
      } catch (e) {
        console.error(e);
      }
    } else {
      acc[keyPrefix] = obj[key];
    }
    return acc;
  }, {} as {[key: string]: {[key: string]: any}});
  return groupedByKeyPrefix;
};
const MAX_VALUES_TO_SHOW = 5000;

type RenderableData =
  | Record<string, unknown>
  | string
  | boolean
  | symbol
  | number
  | HistogramData;

// https://stackoverflow.com/questions/2802341/natural-sort-of-alphanumerical-strings-in-javascript
const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

// 20 is the highest this value goes and we want to show as many significant digits as the user logs
const numberFormatter = Intl.NumberFormat('en-US', {
  maximumFractionDigits: 20,
});

const RenderData: React.FC<RenderDataProps> = React.memo(
  ({
    label,
    data,
    level = 0,
    ignoreUnderscoreKeys = true,
    searchQuery = '',
    parentMatchesSearch = false,
    viewRawData,
    stringifyJsonValue = true,
  }) => {
    const {
      val: dataValue,
      allValidKeysLength,
      shownDataValueLength,
    } = useMemo(() => {
      let val: RenderableData = data?.value ?? data;
      if (val == null) {
        return {val: null, allValidKeysLength: 0, shownDataValueLength: 0};
      }
      const allValidKeys = Object.keys(val).filter(key =>
        ignoreUnderscoreKeys ? !key.startsWith('_') : true
      );

      if (allValidKeys.length > MAX_VALUES_TO_SHOW) {
        const previewKeys = allValidKeys.slice(0, MAX_VALUES_TO_SHOW);
        val = previewKeys.reduce((acc, key) => {
          acc[key] = (val as Record<string, unknown>)[key];
          return acc;
        }, {} as Record<string, unknown>);
      }

      return {
        val,
        allValidKeysLength: allValidKeys.length,
        shownDataValueLength: Object.keys(val).filter(key =>
          ignoreUnderscoreKeys ? !key.startsWith('_') : true
        ).length,
      };
    }, [data, ignoreUnderscoreKeys]);

    const [childrenExpanded, setChildrenExpanded] = useState(
      !Array.isArray(dataValue) &&
        label !== 'gradients' &&
        label !== 'parameters'
    );

    const childKeys = useMemo(() => {
      if (typeof dataValue === 'object' && dataValue != null) {
        return recursiveObjKeys(dataValue);
      } else {
        return [];
      }
    }, [dataValue]);

    const childMatchesSearchQuery = useMemo(() => {
      return (
        searchQuery === '' ||
        childKeys
          .filter(k => (ignoreUnderscoreKeys ? !k.startsWith('_') : true))
          .some(k => k.includes(searchQuery))
      );
    }, [childKeys, ignoreUnderscoreKeys, searchQuery]);

    const matchesSearchQuery = useMemo(() => {
      return searchQuery === '' || label.toLowerCase().includes(searchQuery);
    }, [label, searchQuery]);

    const [searchQueryToPassDown, setSearchQueryToPassDown] =
      useState(searchQuery);

    useEffect(() => {
      if (childrenExpanded && childMatchesSearchQuery) {
        setSearchQueryToPassDown(searchQuery);
      }
    }, [childMatchesSearchQuery, searchQuery, childrenExpanded]);

    const shouldDisplay = useMemo(() => {
      return (
        (ignoreUnderscoreKeys ? !label.startsWith('_') : true) &&
        (matchesSearchQuery || childMatchesSearchQuery || parentMatchesSearch)
      );
    }, [
      ignoreUnderscoreKeys,
      label,
      matchesSearchQuery,
      childMatchesSearchQuery,
      parentMatchesSearch,
    ]);

    const [isHistogram, setIsHistogram] = useState(false);
    const [histogramData, setHistogramData] = useState<PlotHelpers.Bar[]>([]);

    useEffect(() => {
      if ((dataValue as HistogramData)?._type === 'histogram') {
        const histogramValue = dataValue as HistogramData;
        if (histogramValue.bins && histogramValue.values) {
          const dataPoints: PlotHelpers.Bar[] = histogramValue.values.map(
            (elem: number, index: number) => {
              const key =
                histogramValue.bins && histogramValue.bins.length > index
                  ? histogramValue.bins[index].toPrecision(4)
                  : '-';
              return {
                key,
                value: elem,
                color: globals.darkBlue,
                title: 'x= ' + key + ', y=',
              };
            }
          );
          setHistogramData(dataPoints);
          setIsHistogram(true);
        }
      }
    }, [dataValue]);

    return (
      <div
        style={{
          display: shouldDisplay ? 'block' : 'none',
        }}
        key={`${label}${level}`}>
        {typeof dataValue !== 'object' || dataValue == null ? (
          <S.Row>
            <S.LabelCell level={level}>{label}</S.LabelCell>
            <S.ValueCell>
              {typeof dataValue === 'string' && isUrl(dataValue) ? (
                <TargetBlank href={dataValue}>{dataValue}</TargetBlank>
              ) : typeof dataValue === 'string' && !stringifyJsonValue ? (
                dataValue
              ) : typeof dataValue === 'number' ? (
                numberFormatter.format(dataValue)
              ) : (
                JSON.stringify(dataValue)
              )}
            </S.ValueCell>
          </S.Row>
        ) : isHistogram && histogramData.length > 0 ? (
          <S.Row>
            <S.LabelCell level={level}>{label}</S.LabelCell>
            <S.ValueCell extraPadding={true}>
              <BarChart
                bars={histogramData}
                vertical={true}
                height={150}></BarChart>
            </S.ValueCell>
          </S.Row>
        ) : (
          <>
            <S.Row>
              <S.LabelCell
                isParent
                level={level}
                onClick={() => {
                  setChildrenExpanded(curr => !curr);
                }}>
                {childrenExpanded ? (
                  <S.CaretIcon name="down" />
                ) : (
                  <S.CaretIcon name="next" />
                )}{' '}
                {label}{' '}
                {childrenExpanded ? '' : `(${allValidKeysLength} collapsed)`}
              </S.LabelCell>
            </S.Row>
            <div
              style={{
                display: childrenExpanded && shouldDisplay ? 'block' : 'none',
              }}>
              {Object.keys(dataValue)
                .sort(collator.compare)
                .map(key => (
                  <RenderData
                    key={`${key}${level}`}
                    label={key}
                    data={(dataValue as Record<string, unknown>)[key]}
                    level={level + 1}
                    searchQuery={searchQueryToPassDown}
                    parentMatchesSearch={matchesSearchQuery}
                    ignoreUnderscoreKeys={ignoreUnderscoreKeys}
                    stringifyJsonValue={stringifyJsonValue}
                  />
                ))}
              {shownDataValueLength < allValidKeysLength && searchQuery === '' && (
                <S.Row>
                  <S.OverflowInfo level={level}>
                    +{allValidKeysLength - shownDataValueLength} more not shown.{' '}
                    {viewRawData && (
                      <TargetBlank onClick={viewRawData}>
                        View raw data
                      </TargetBlank>
                    )}
                  </S.OverflowInfo>
                </S.Row>
              )}
            </div>
          </>
        )}
      </div>
    );
  }
);

type CollapsibleJSONTableProps = {
  data: any; // Data to render
  ignoreUnderscoreKeys?: boolean; // Ignore keys starting with "_"; defaults to false
  ignoreWandbKey?: boolean; // Ignore "_wandb" key; defaults to true
  viewRawData?: () => void;
  stringifyJsonValue?: boolean; // Call JSON.stringify on values (wraps strings in quotation marks)
};

const CollapsibleJSONTable = ({
  data,
  ignoreUnderscoreKeys = false,
  ignoreWandbKey = true,
  viewRawData,
  stringifyJsonValue = true,
}: CollapsibleJSONTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const groupedData = useMemo(() => {
    if (data != null && typeof data === 'object' && !Array.isArray(data)) {
      try {
        return groupByKeyPrefix(data);
      } catch (e) {
        console.error(e);
        return data;
      }
    } else {
      return data;
    }
  }, [data]);

  return (
    <S.TableWrapper>
      <S.StickySearchBar>
        <S.TextInput
          icon={{
            className: 'wbic-ic-search',
          }}
          iconPosition="left"
          placeholder="Search keys"
          onChange={(e: React.FormEvent<HTMLInputElement>) => {
            setSearchQuery(
              (e.target as HTMLInputElement).value.toLowerCase().trim()
            );
          }}
        />
      </S.StickySearchBar>
      <S.StickyHeader>
        <S.HeaderCell isKeyHeader>Key</S.HeaderCell>
        <S.HeaderCell>Value</S.HeaderCell>
      </S.StickyHeader>
      {typeof groupedData === 'object' &&
        groupedData != null &&
        Object.keys(groupedData)
          .filter(key => (ignoreWandbKey ? key !== '_wandb' : true))
          .sort()
          .map(key => (
            <RenderData
              key={key}
              label={key}
              data={groupedData[key]}
              ignoreUnderscoreKeys={ignoreUnderscoreKeys}
              searchQuery={searchQuery}
              viewRawData={viewRawData}
              stringifyJsonValue={stringifyJsonValue}
            />
          ))}
    </S.TableWrapper>
  );
};

export default CollapsibleJSONTable;
